import { useEffect, useRef, useState } from "react";
import { motion, useInView, AnimatePresence } from "framer-motion";
import axios from "axios";
import ShareBar from "./ShareBar";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const DONATE_GSG = "https://www.givesendgo.com/justicefordanielle?utm_source=share";
const DONATE_FNF = "https://freenowfoundation.app.neoncrm.com/forms/28";
const LS_KEY = "danielle_candle_lit_at";
const LS_ENTRY_KEY = "danielle_candle_entry_id";
const MSG_MAX = 140;
const MAX_RENDERED = 240;

function fmt(n) {
    return n.toLocaleString("en-US");
}

/**
 * A single small candle SVG. Lit = amber flame + glow, unlit = grey wick.
 */
function Candle({ lit, index, isNext, size = "sm" }) {
    const seed = (index * 37) % 100;
    const flickerDuration = 0.55 + (seed % 30) / 100;
    const glowDuration = 1.6 + (seed % 40) / 30;
    const isLg = size === "lg";
    return (
        <motion.div
            initial={{ opacity: 0, y: 8, scale: 0.85 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            transition={{
                duration: 0.7,
                delay: Math.min(0.03 * (index % 40), 1.2),
                ease: [0.22, 1, 0.36, 1],
            }}
            className={`relative flex flex-col items-center justify-end ${
                isLg ? "w-8 sm:w-10 h-24 sm:h-28" : "w-6 sm:w-7 h-16 sm:h-20"
            }`}
            aria-hidden="true"
        >
            {lit && (
                <motion.div
                    aria-hidden="true"
                    animate={{
                        opacity: [0.4, 0.85, 0.55, 0.9, 0.5],
                        scale: [0.9, 1.15, 0.95, 1.1, 0.9],
                    }}
                    transition={{
                        duration: glowDuration,
                        repeat: Infinity,
                        ease: "easeInOut",
                    }}
                    className={`absolute left-1/2 -translate-x-1/2 bottom-[38%] rounded-full pointer-events-none ${
                        isLg ? "w-14 sm:w-16 h-14 sm:h-16" : "w-10 sm:w-12 h-10 sm:h-12"
                    }`}
                    style={{
                        background:
                            "radial-gradient(circle, rgba(255,190,110,0.55) 0%, rgba(255,180,90,0.28) 45%, rgba(255,160,60,0) 75%)",
                        filter: "blur(2px)",
                    }}
                />
            )}
            <motion.div
                animate={
                    lit
                        ? {
                              scaleY: [1, 1.22, 0.78, 1.18, 0.9, 1.12, 0.95, 1],
                              scaleX: [1, 0.82, 1.15, 0.88, 1.08, 0.9, 1.04, 1],
                              opacity: [0.85, 1, 0.7, 1, 0.78, 1, 0.9, 0.95],
                              rotate: [0, -4, 3, -2, 4, -3, 2, 0],
                              x: [0, -0.6, 0.6, -0.4, 0.5, -0.3, 0.2, 0],
                          }
                        : { opacity: 0.3, scale: 0.95 }
                }
                transition={
                    lit
                        ? {
                              duration: flickerDuration,
                              repeat: Infinity,
                              ease: "easeInOut",
                              times: [0, 0.14, 0.28, 0.42, 0.56, 0.7, 0.85, 1],
                          }
                        : { duration: 1 }
                }
                className="relative mb-[-3px] z-10"
                style={{ transformOrigin: "50% 100%" }}
            >
                <div
                    className={
                        lit
                            ? `${isLg ? "w-3 h-5" : "w-2 h-3.5 sm:w-2.5 sm:h-4"} bg-gradient-to-b from-yellow-100 via-amber-200 to-orange-500 shadow-[0_0_18px_6px_rgba(255,190,110,0.7),0_0_36px_12px_rgba(255,150,60,0.35)]`
                            : "w-1 h-2 bg-neutral-600"
                    }
                    style={{ borderRadius: "50% 50% 50% 50% / 60% 60% 40% 40%" }}
                />
            </motion.div>
            <div className="w-[1.5px] h-1.5 bg-neutral-800 relative z-10" />
            {lit ? (
                <motion.div
                    animate={{
                        boxShadow: [
                            "0 -6px 14px 0 rgba(255,190,110,0.20)",
                            "0 -6px 20px 2px rgba(255,190,110,0.35)",
                            "0 -6px 12px 0 rgba(255,190,110,0.18)",
                            "0 -6px 22px 3px rgba(255,190,110,0.40)",
                            "0 -6px 14px 0 rgba(255,190,110,0.20)",
                        ],
                    }}
                    transition={{
                        duration: flickerDuration * 1.3,
                        repeat: Infinity,
                        ease: "easeInOut",
                    }}
                    className={`${
                        isLg ? "w-5 h-16" : "w-3 sm:w-4 h-10 sm:h-12"
                    } bg-gradient-to-b from-neutral-100 via-neutral-200 to-neutral-400`}
                />
            ) : (
                <div
                    className={
                        isNext
                            ? `${isLg ? "w-5 h-16" : "w-3 sm:w-4 h-10 sm:h-12"} bg-neutral-800/40 border border-dashed border-neutral-500`
                            : `${isLg ? "w-5 h-16" : "w-3 sm:w-4 h-10 sm:h-12"} bg-gradient-to-b from-neutral-100 via-neutral-200 to-neutral-400`
                    }
                />
            )}
        </motion.div>
    );
}

/**
 * Post-light modal offering two donation options.
 * Choosing one marks the just-lit candle as donated (adds amber ♥).
 */
function DonateChoiceModal({ open, onClose, onDonate, name }) {
    return (
        <AnimatePresence>
            {open && (
                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.4 }}
                    className="fixed inset-0 z-[80] flex items-center justify-center px-6"
                    role="dialog"
                    aria-modal="true"
                    aria-label="Donate to Danielle's case"
                    data-testid="donate-choice-modal"
                >
                    <div
                        className="absolute inset-0 bg-neutral-950/85 backdrop-blur-md"
                        onClick={onClose}
                    />
                    <motion.div
                        initial={{ opacity: 0, y: 30, scale: 0.96 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        exit={{ opacity: 0, y: 20, scale: 0.97 }}
                        transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
                        className="relative w-full max-w-2xl bg-neutral-950 border border-neutral-700 text-neutral-50 p-8 sm:p-12"
                    >
                        <div className="font-sans text-[10px] uppercase tracking-[0.35em] text-amber-300">
                            Your candle is lit
                        </div>
                        <h3 className="mt-4 font-serif text-3xl sm:text-4xl leading-tight text-neutral-50">
                            Thank you{name ? `, ${name}` : ""}.
                            <br />
                            <span className="italic font-light text-neutral-300">
                                Would you like to stand with Danielle?
                            </span>
                        </h3>
                        <p className="mt-4 font-serif text-base sm:text-lg text-neutral-300 leading-relaxed">
                            Every dollar funds expert witnesses and court fees.
                            Choose where to give — both go to Danielle&rsquo;s
                            fight.
                        </p>

                        <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-4">
                            <button
                                type="button"
                                onClick={() => onDonate("givesendgo")}
                                data-testid="modal-donate-gsg"
                                className="group relative border border-neutral-200 px-6 py-6 text-left transition-colors duration-500 hover:bg-neutral-50 hover:text-neutral-950"
                            >
                                <div className="font-sans text-[10px] uppercase tracking-[0.3em] text-neutral-400 group-hover:text-neutral-600">
                                    Legal fund
                                </div>
                                <div className="mt-2 font-serif text-xl sm:text-2xl leading-tight">
                                    GiveSendGo
                                </div>
                                <div className="mt-1 font-sans text-[10px] uppercase tracking-[0.3em] text-neutral-500 group-hover:text-neutral-700">
                                    Justice for Danielle →
                                </div>
                            </button>
                            <button
                                type="button"
                                onClick={() => onDonate("freenow")}
                                data-testid="modal-donate-fnf"
                                className="group relative border border-neutral-200 px-6 py-6 text-left transition-colors duration-500 hover:bg-neutral-50 hover:text-neutral-950"
                            >
                                <div className="font-sans text-[10px] uppercase tracking-[0.3em] text-neutral-400 group-hover:text-neutral-600">
                                    501(c)(3) · Tax-deductible
                                </div>
                                <div className="mt-2 font-serif text-xl sm:text-2xl leading-tight">
                                    Free Now Foundation
                                </div>
                                <div className="mt-1 font-sans text-[10px] uppercase tracking-[0.3em] text-neutral-500 group-hover:text-neutral-700">
                                    Give via NeonCRM →
                                </div>
                            </button>
                        </div>

                        <button
                            type="button"
                            onClick={onClose}
                            data-testid="modal-not-now"
                            className="mt-8 font-sans text-[10px] uppercase tracking-[0.35em] text-neutral-400 hover:text-neutral-100 transition-colors"
                        >
                            Not now — keep my candle lit
                        </button>
                    </motion.div>
                </motion.div>
            )}
        </AnimatePresence>
    );
}

export default function CandleTribute() {
    const sectionRef = useRef(null);
    const inView = useInView(sectionRef, { once: true, margin: "-15%" });

    const [count, setCount] = useState(0);
    const [displayed, setDisplayed] = useState(0);
    const [entries, setEntries] = useState([]);
    const [name, setName] = useState("");
    const [message, setMessage] = useState("");
    const [lit, setLit] = useState(false);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState("");
    const [modalOpen, setModalOpen] = useState(false);
    const [lastEntryId, setLastEntryId] = useState(null);
    const [lastName, setLastName] = useState("");
    // Session-only — resets on page refresh, so confirmation shows once per session
    const [justLit, setJustLit] = useState(false);
    const [donatedThisSession, setDonatedThisSession] = useState(false);

    useEffect(() => {
        (async () => {
            try {
                const r = await axios.get(`${API}/candles/wall`);
                setCount(r.data.count || 0);
                setEntries(r.data.entries || []);
            } catch {
                /* silent */
            }
            if (localStorage.getItem(LS_KEY)) setLit(true);
        })();
    }, []);

    useEffect(() => {
        if (!inView) return;
        const from = displayed;
        const to = count;
        if (from === to) return;
        const dur = 1400;
        const start = performance.now();
        let raf;
        const tick = (t) => {
            const p = Math.min(1, (t - start) / dur);
            const eased = 1 - Math.pow(1 - p, 3);
            setDisplayed(Math.round(from + (to - from) * eased));
            if (p < 1) raf = requestAnimationFrame(tick);
        };
        raf = requestAnimationFrame(tick);
        return () => cancelAnimationFrame(raf);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [inView, count]);

    const lightCandle = async (e) => {
        e?.preventDefault?.();
        if (lit || busy) return;
        const trimmedName = name.trim();
        if (!trimmedName) {
            setError("Please add a name.");
            return;
        }
        setError("");
        setBusy(true);
        try {
            const r = await axios.post(`${API}/candles/light`, {
                name: trimmedName,
                message: message.trim().slice(0, MSG_MAX),
                donated: false,
            });
            setCount(r.data.count);
            setEntries(r.data.entries || []);
            setLit(true);
            setLastName(trimmedName);
            const newestEntry = (r.data.entries || []).find(
                (x) => x.name === trimmedName,
            );
            if (newestEntry) {
                setLastEntryId(newestEntry.id);
                localStorage.setItem(LS_ENTRY_KEY, newestEntry.id);
            }
            localStorage.setItem(LS_KEY, new Date().toISOString());
            setJustLit(true);
        } catch {
            setError("Something went wrong. Please try again.");
        } finally {
            setBusy(false);
        }
    };

    const openDonation = async (which) => {
        const url = which === "freenow" ? DONATE_FNF : DONATE_GSG;
        if (lastEntryId && !donatedThisSession) {
            try {
                await axios.post(`${API}/candles/${lastEntryId}/donate`);
                setEntries((prev) =>
                    prev.map((e) =>
                        e.id === lastEntryId ? { ...e, donated: true } : e,
                    ),
                );
                setDonatedThisSession(true);
            } catch {
                /* silent — donation link still opens */
            }
        }
        window.open(url, "_blank", "noopener,noreferrer");
    };

    const scrollToCandles = () => {
        const el = document.querySelector('[data-testid="candle-wall"]');
        if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
    };

    const renderedCount = Math.min(count, MAX_RENDERED);
    const overflow = Math.max(0, count - MAX_RENDERED);
    const candlesArray = Array.from({ length: renderedCount });

    return (
        <section
            ref={sectionRef}
            data-testid="candle-section"
            id="candle"
            className="relative w-full bg-neutral-950 text-neutral-50 py-24 sm:py-32 border-t border-neutral-800 overflow-hidden"
        >
            <div className="max-w-[92rem] mx-auto px-6 sm:px-12 lg:px-16">
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-10%" }}
                    transition={{ duration: 1 }}
                    className="grid grid-cols-12 gap-6 items-end mb-14 sm:mb-20"
                >
                    <div className="col-span-12 sm:col-span-3 font-sans text-[10px] uppercase tracking-[0.35em] text-neutral-400">
                        Chapter VIII — Light a candle
                    </div>
                    <div className="col-span-12 sm:col-span-9">
                        <h2 className="font-serif text-5xl sm:text-7xl lg:text-8xl leading-[0.9] tracking-tight text-neutral-50">
                            Light a candle
                            <br />
                            <span className="italic font-light text-neutral-300">
                                for Danielle.
                            </span>
                        </h2>
                        <p className="mt-6 max-w-2xl font-serif italic text-lg sm:text-xl text-neutral-300">
                            Write a name — your own, a loved one&rsquo;s, or one
                            you are carrying — and let a candle burn beside
                            Danielle&rsquo;s. Every candle keeps her name alive.
                        </p>
                    </div>
                </motion.div>

                {/* Form + Counter (or Confirmation after lighting) */}
                <div className="grid grid-cols-12 gap-8 sm:gap-12 mb-16 sm:mb-24">
                    <div className="col-span-12 sm:col-span-5">
                        <div
                            className="font-sans tabular font-extralight leading-none tracking-[-0.03em] text-neutral-50"
                            style={{ fontSize: "clamp(4rem, 10vw, 9rem)" }}
                            data-testid="candle-count"
                        >
                            {fmt(displayed)}
                        </div>
                        <div className="mt-3 font-sans text-xs uppercase tracking-[0.32em] text-neutral-400">
                            candles lit · in her name
                        </div>
                    </div>

                    <div className="col-span-12 sm:col-span-7">
                        <AnimatePresence mode="wait">
                            {!justLit ? (
                                <motion.form
                                    key="form"
                                    initial={{ opacity: 0 }}
                                    animate={{ opacity: 1 }}
                                    exit={{ opacity: 0, y: -8 }}
                                    transition={{ duration: 0.5 }}
                                    onSubmit={lightCandle}
                                    data-testid="candle-form"
                                    className="flex flex-col gap-5"
                                >
                                    <div>
                                        <label
                                            htmlFor="candle-name"
                                            className="block font-sans text-[10px] uppercase tracking-[0.35em] text-neutral-400 mb-2"
                                        >
                                            Whose name burns on this candle?
                                        </label>
                                        <input
                                            id="candle-name"
                                            type="text"
                                            value={name}
                                            onChange={(e) => setName(e.target.value.slice(0, 80))}
                                            disabled={lit || busy}
                                            data-testid="candle-name-input"
                                            placeholder="e.g. Danielle, Mom, In memory of…"
                                            className="w-full bg-transparent border-b border-neutral-500 focus:border-neutral-100 outline-none font-serif text-2xl sm:text-3xl py-2 text-neutral-50 placeholder:text-neutral-600 transition-colors disabled:opacity-60"
                                            maxLength={80}
                                        />
                                    </div>
                                    <div>
                                        <label
                                            htmlFor="candle-message"
                                            className="flex items-center justify-between font-sans text-[10px] uppercase tracking-[0.35em] text-neutral-400 mb-2"
                                        >
                                            <span>A short message (optional)</span>
                                            <span className="tabular text-neutral-500">
                                                {message.length}/{MSG_MAX}
                                            </span>
                                        </label>
                                        <textarea
                                            id="candle-message"
                                            value={message}
                                            onChange={(e) =>
                                                setMessage(e.target.value.slice(0, MSG_MAX))
                                            }
                                            disabled={lit || busy}
                                            data-testid="candle-message-input"
                                            rows={2}
                                            placeholder="A word, a prayer, a promise."
                                            className="w-full bg-transparent border border-neutral-800 focus:border-neutral-500 outline-none font-serif text-base sm:text-lg p-3 text-neutral-100 placeholder:text-neutral-600 transition-colors resize-none disabled:opacity-60"
                                            maxLength={MSG_MAX}
                                        />
                                    </div>

                                    {error && (
                                        <div
                                            data-testid="candle-error"
                                            className="font-sans text-[10px] uppercase tracking-[0.3em] text-amber-300"
                                        >
                                            {error}
                                        </div>
                                    )}

                                    <button
                                        type="submit"
                                        disabled={lit || busy}
                                        data-testid="light-candle-button"
                                        className="group relative self-start mt-2 border border-neutral-200 px-8 py-5 min-h-[48px] font-sans text-xs uppercase tracking-[0.35em] text-neutral-50 hover:text-neutral-950 transition-colors duration-700 disabled:cursor-default disabled:hover:text-neutral-400 disabled:text-neutral-400 disabled:border-neutral-700"
                                    >
                                        <span className="absolute inset-0 origin-bottom scale-y-0 group-hover:scale-y-100 group-disabled:scale-y-0 bg-neutral-50 transition-transform duration-700 ease-[cubic-bezier(0.77,0,0.175,1)]" />
                                        <span className="relative flex items-center gap-3">
                                            {lit
                                                ? "Candle lit — thank you"
                                                : busy
                                                  ? "Lighting…"
                                                  : "Light the Candle"}
                                            {!lit && (
                                                <svg
                                                    width="30"
                                                    height="12"
                                                    viewBox="0 0 30 12"
                                                    fill="none"
                                                    aria-hidden="true"
                                                    className="transition-transform duration-500 group-hover:translate-x-2"
                                                >
                                                    <path
                                                        d="M0 6 H28 M22 1 L28 6 L22 11"
                                                        stroke="currentColor"
                                                        strokeWidth="1"
                                                        fill="none"
                                                    />
                                                </svg>
                                            )}
                                        </span>
                                    </button>

                                    <p className="mt-1 font-sans text-[10px] uppercase tracking-[0.32em] text-neutral-500">
                                        After lighting you can choose to donate — GiveSendGo
                                        or Free Now Foundation. Not required.
                                    </p>
                                </motion.form>
                            ) : (
                                <motion.div
                                    key="confirmation"
                                    initial={{ opacity: 0, y: 20 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    exit={{ opacity: 0 }}
                                    transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
                                    data-testid="candle-confirmation"
                                    className="flex flex-col gap-6"
                                >
                                    <div className="inline-flex items-center gap-3 self-start font-sans text-[10px] uppercase tracking-[0.35em] text-amber-300">
                                        <span
                                            aria-hidden="true"
                                            className="w-1.5 h-1.5 rounded-full bg-amber-400 animate-pulse"
                                        />
                                        Your candle is lit
                                    </div>

                                    <h3 className="font-serif text-3xl sm:text-4xl lg:text-5xl leading-[1.05] tracking-tight text-neutral-50">
                                        Your candle is lit.{" "}
                                        <span className="italic font-light text-neutral-300">
                                            Danielle&rsquo;s name stays alive
                                            because of you.
                                        </span>
                                    </h3>

                                    <div className="space-y-5 font-serif italic text-lg sm:text-xl text-neutral-300 leading-relaxed max-w-xl">
                                        <p>
                                            Thank you.{" "}
                                            <span className="not-italic text-neutral-50 font-medium">
                                                {lastName || "Your candle"}
                                            </span>{" "}
                                            now burns beside Danielle&rsquo;s —
                                            one more light in the dark, one more
                                            person who refused to look away.
                                        </p>
                                        <p>
                                            If her story moved you enough to light
                                            a flame, please consider one more step.
                                            Expert witnesses and court fees are
                                            what stand between her mother&rsquo;s
                                            promise and the truth.
                                        </p>
                                        <p className="not-italic text-neutral-50">
                                            A candle keeps her name alive. A
                                            donation keeps her case alive.
                                        </p>
                                    </div>

                                    <div className="flex flex-col gap-4 pt-2">
                                        <button
                                            type="button"
                                            onClick={() => openDonation("givesendgo")}
                                            data-testid="confirmation-donate-gsg"
                                            className="group relative self-start bg-neutral-50 text-neutral-950 hover:bg-amber-400 hover:text-neutral-950 transition-colors duration-500 px-8 py-5 min-h-[48px] font-sans text-xs sm:text-sm uppercase tracking-[0.25em] font-bold"
                                        >
                                            <span className="flex items-center gap-3">
                                                <span>Keep her case alive — Donate</span>
                                                <span aria-hidden="true">→</span>
                                            </span>
                                        </button>

                                        <button
                                            type="button"
                                            onClick={() => openDonation("freenow")}
                                            data-testid="confirmation-donate-fnf"
                                            className="self-start font-sans text-[11px] uppercase tracking-[0.3em] text-neutral-300 hover:text-neutral-50 transition-colors underline underline-offset-4 decoration-neutral-600"
                                        >
                                            Prefer a tax-deductible gift? Give through Free Now Foundation, 501(c)(3) →
                                        </button>

                                        <div className="pt-4">
                                            <ShareBar
                                                variant="dark"
                                                label="Can't give today? Share her story instead"
                                                text="Danielle walked into a hospital with a cough. Forty-one days later she was gone. Read her story. Stand with her family. #JusticeForDanielle #DanielleAlvarez"
                                            />
                                        </div>

                                        <div className="pt-2 flex items-center gap-4 font-sans text-[10px] uppercase tracking-[0.3em]">
                                            <span className="text-amber-300 font-semibold not-italic">
                                                For Danielle. Always for Danielle. 🧡
                                            </span>
                                        </div>

                                        <button
                                            type="button"
                                            onClick={scrollToCandles}
                                            data-testid="confirmation-back"
                                            className="self-start mt-1 font-sans text-[10px] uppercase tracking-[0.35em] text-neutral-500 hover:text-neutral-200 transition-colors"
                                        >
                                            ← Back to the candles
                                        </button>
                                    </div>
                                </motion.div>
                            )}
                        </AnimatePresence>
                    </div>
                </div>

                {/* Donation appeal — sits above the candle wall */}
                <motion.div
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-10%" }}
                    transition={{ duration: 1, ease: [0.22, 1, 0.36, 1] }}
                    data-testid="donation-appeal"
                    className="mb-14 sm:mb-20 grid grid-cols-12 gap-6 sm:gap-10 items-start"
                >
                    <div className="col-span-12 sm:col-span-4">
                        <h3 className="font-serif text-4xl sm:text-5xl lg:text-6xl leading-[0.98] tracking-tight text-neutral-50">
                            Every candle{" "}
                            <span className="italic font-light text-neutral-300">
                                is a promise.
                            </span>
                            <br />
                            Every donation{" "}
                            <span className="italic font-light text-neutral-300">
                                is a fight.
                            </span>
                        </h3>
                    </div>
                    <div className="col-span-12 sm:col-span-8 flex flex-col gap-6">
                        <p className="font-serif italic text-lg sm:text-xl leading-relaxed text-neutral-200">
                            <span className="tabular text-neutral-50 not-italic font-semibold">
                                {count > 0 ? fmt(count) : "Fifteen"}
                            </span>{" "}
                            candles burn for Danielle. Behind each one is a
                            person who stopped, read her story, and refused to
                            look away.
                        </p>
                        <p className="font-serif italic text-lg sm:text-xl leading-relaxed text-neutral-200">
                            But candles alone cannot walk into a courtroom.
                            Expert witnesses, medical record reviews, and court
                            fees — this is what justice costs. This is what your
                            donation pays for.
                        </p>
                        <p className="font-serif italic text-lg sm:text-xl leading-relaxed text-neutral-200">
                            Danielle walked into the hospital with a cough. She
                            never walked out. Rebecca prepared and filed the
                            case herself, pro se, before retaining counsel.{" "}
                            <span className="not-italic text-neutral-50">
                                No attorney, no legal training,
                            </span>{" "}
                            just a promise to be her daughter&rsquo;s voice.
                        </p>
                        <p className="font-serif text-lg sm:text-xl leading-relaxed text-neutral-50">
                            Light a candle to remember her. Give what you can to
                            fight for her.{" "}
                            <span className="italic text-neutral-300">
                                Do both, and you become part of the answer.
                            </span>
                        </p>

                        <div className="mt-4 flex flex-col gap-4">
                            <a
                                href={DONATE_GSG}
                                target="_blank"
                                rel="noopener noreferrer"
                                data-testid="appeal-donate-gsg"
                                className="group relative self-start bg-neutral-50 text-neutral-950 hover:bg-amber-400 transition-colors duration-500 px-8 py-5 min-h-[48px] font-sans text-xs sm:text-sm uppercase tracking-[0.25em] font-bold inline-flex items-center gap-3"
                            >
                                <span>Donate on GiveSendGo</span>
                                <span aria-hidden="true">→</span>
                            </a>
                            <p className="font-serif italic text-sm sm:text-base text-neutral-400">
                                Tax-deductible option available through{" "}
                                <a
                                    href={DONATE_FNF}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    data-testid="appeal-donate-fnf"
                                    className="text-neutral-200 underline underline-offset-4 decoration-neutral-500 hover:text-neutral-50 transition-colors"
                                >
                                    Free Now Foundation
                                </a>
                                , 501(c)(3). Both funds go to the fight.
                            </p>
                            <p className="font-sans text-xs sm:text-sm uppercase tracking-[0.25em] text-amber-300 font-semibold">
                                For Danielle. Always for Danielle. 🧡
                            </p>
                        </div>
                    </div>
                </motion.div>

                {/* Visual flame wall */}
                <div
                    data-testid="candle-wall"
                    className="relative w-full border-t border-b border-neutral-800/80 py-10 sm:py-14 mb-14 sm:mb-20"
                >
                    <div className="flex flex-wrap gap-x-3 sm:gap-x-4 gap-y-6 sm:gap-y-8 justify-start items-end">
                        <AnimatePresence initial={false}>
                            {candlesArray.map((_, i) => (
                                <Candle key={`lit-${i}`} lit={true} index={i} />
                            ))}
                            <Candle
                                key="next"
                                lit={false}
                                index={renderedCount}
                                isNext
                            />
                        </AnimatePresence>
                    </div>

                    {overflow > 0 && (
                        <div className="mt-6 font-sans tabular text-[10px] uppercase tracking-[0.3em] text-neutral-500">
                            + {fmt(overflow)} more lit
                        </div>
                    )}
                </div>

                {/* Names wall */}
                {entries.length > 0 && (
                    <div
                        data-testid="candle-names-wall"
                        className="grid grid-cols-12 gap-6"
                    >
                        <div className="col-span-12 sm:col-span-3 font-sans text-[10px] uppercase tracking-[0.35em] text-neutral-400">
                            Names lit for Danielle · latest
                        </div>
                        <ul className="col-span-12 sm:col-span-9 divide-y divide-neutral-800/80">
                            {entries.map((entry) => (
                                <li
                                    key={entry.id}
                                    data-testid="candle-entry"
                                    className="py-5 grid grid-cols-12 gap-4 items-start"
                                >
                                    <div className="col-span-12 sm:col-span-1 flex items-start">
                                        <div className="scale-75 origin-top-left -mt-1">
                                            <Candle
                                                lit={true}
                                                index={0}
                                                size="sm"
                                            />
                                        </div>
                                    </div>
                                    <div className="col-span-12 sm:col-span-11">
                                        <div className="font-serif text-xl sm:text-2xl text-neutral-50 flex items-center gap-2 flex-wrap">
                                            <span>{entry.name}</span>
                                            {entry.donated && (
                                                <span
                                                    className="text-amber-400 text-base"
                                                    aria-label="donated"
                                                    title="Donated to Danielle's case"
                                                >
                                                    ♥
                                                </span>
                                            )}
                                        </div>
                                        {entry.message && (
                                            <p className="mt-2 font-serif italic text-base sm:text-lg text-neutral-300 leading-snug max-w-3xl">
                                                &ldquo;{entry.message}&rdquo;
                                            </p>
                                        )}
                                    </div>
                                </li>
                            ))}
                        </ul>
                    </div>
                )}
            </div>
        </section>
    );
}
